import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { MaturityBadge } from '@reflex/ui/components/shared/MaturityBadge';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { ConnectionIndicator } from '@/components/status/ConnectionIndicator';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { ROUTES } from '@/lib/routes';
import { PRODUCT_MATURITY_STAGE } from '@/lib/product-stage';
import type { NavItem, NavDrillGroup } from './nav-types';
import {
  getActiveDrillGroup,
  drillInVariants,
  drillOutVariants,
  slideTransition,
} from './nav-utils';
import { NavDrillItem, NavDrillContent, NavBottomSection } from './NavDrill';
import { NavMainLinks, NavExpandView, NavFlatView, NavFlyoutView } from './NavStyleViews';
import { DEFAULT_NAV_STYLE, type NavStyle } from './useNavStyle';
import { OrgSwitcher } from './OrgSwitcher';
import { ReflexMark } from '@reflex/ui/components/brand/ReflexMark';
import { UserSection } from './UserSection';
import type { WsConnectionState } from '@/lib/socketManager';

interface NavBarExpandedProps {
  wsState: WsConnectionState;
  onCommandOpen: () => void;
  allMainNavItems: NavItem[];
  drillGroups: NavDrillGroup[];
  mainDrillGroups: NavDrillGroup[];
  bottomDrillGroups: NavDrillGroup[];
  pluginBottomItems: NavItem[];
  /** Presentation style for section groups. Defaults to {@link DEFAULT_NAV_STYLE}. */
  navStyle?: NavStyle;
}

export function NavBarExpanded({
  wsState,
  onCommandOpen,
  allMainNavItems,
  drillGroups,
  mainDrillGroups,
  bottomDrillGroups,
  pluginBottomItems,
  navStyle = DEFAULT_NAV_STYLE,
}: NavBarExpandedProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slug = useActiveOrgSlug();
  const homePath = slug ? ROUTES.org(slug).home : '/';

  // URL-based active drill group (used for nav item highlighting)
  const urlDrillGroup = getActiveDrillGroup(pathname, drillGroups);

  // Explicit drill state: when true, the drill view is forced closed even if
  // the URL matches a drill group. This allows the back button to close the
  // drill from a child route without navigating away from that page.
  const [drillClosed, setDrillClosed] = useState(false);

  // Track the previous URL drill group to detect changes. When the URL changes
  // to a different drill group, reset drillClosed so the new group opens.
  // This is the "deriving state from props" pattern that avoids useEffect.
  const prevDrillRef = useRef(urlDrillGroup?.basePath);
  if (prevDrillRef.current !== urlDrillGroup?.basePath) {
    prevDrillRef.current = urlDrillGroup?.basePath;
    if (drillClosed) {
      setDrillClosed(false);
    }
  }

  const closeDrill = useCallback(() => {
    // If the user is sitting on the drill's overview page exactly, navigate
    // home so the URL — and therefore the sidebar highlight — clears. Without
    // this, clicking "Misc" → "Back" would leave the URL at `/misc` and the
    // Misc category would stay visually selected even though the panel closed.
    // From a child route (e.g. `/misc/mcp-setup`) we keep the URL and only
    // close the panel visually, since the user is still genuinely in that
    // section.
    if (urlDrillGroup && pathname === urlDrillGroup.basePath) {
      void navigate(homePath);
      return;
    }
    setDrillClosed(true);
  }, [urlDrillGroup, pathname, navigate, homePath]);

  // The displayed drill group is the URL-based group unless explicitly closed
  const activeDrillGroup = drillClosed ? null : urlDrillGroup;
  const viewKey = activeDrillGroup ? `drill:${activeDrillGroup.basePath}` : 'top-level';
  const variants = activeDrillGroup ? drillInVariants : drillOutVariants;
  // Items in the active panel whose `to` matches one of these basePaths
  // render with a chevron, signalling they open a nested drill panel
  // (e.g. Organization → Integrations).
  const subDrillBasePaths = useMemo(
    () => new Set(drillGroups.filter((g) => g.parentBasePath).map((g) => g.basePath)),
    [drillGroups],
  );

  return (
    <nav aria-label="Main navigation" className="flex h-full w-full min-w-0 flex-col bg-sidebar">
      <div className="@container flex h-11 shrink-0 items-center justify-between px-3">
        <div className="flex min-w-0 items-center gap-2">
          <Link to={homePath} className="flex min-w-0 items-center gap-2" aria-label="Go home">
            <ReflexMark variant="tile" />
            <PanelTitle
              as="span"
              size="sm"
              className="truncate font-semibold tracking-tighter text-sidebar-foreground"
            >
              Reflex
            </PanelTitle>
          </Link>
          {/* The stage badge is the lowest-priority element in this row: when the
              sidebar is narrow it collapses out (container query) rather than
              squeezing the logo or wordmark. The query resolves against this
              header's content box, which is the sidebar width minus its `px-3`
              padding (24px). At the default 224px sidebar (see NAV_DEFAULT_WIDTH
              in Layout.tsx) that content box is ~200px, so the threshold sits at
              11rem (176px) — comfortably below the default so the badge shows out
              of the box, yet still hidden as the sidebar approaches its 180px
              minimum. */}
          <span className="hidden shrink-0 @[11rem]:flex">
            <MaturityBadge stage={PRODUCT_MATURITY_STAGE} size="pill" />
          </span>
          <ConnectionIndicator state={wsState} iconOnly />
        </div>
      </div>
      <div className="accent-line" />
      <div className="px-1.5 py-2">
        <button
          type="button"
          onClick={onCommandOpen}
          className="group flex w-full items-center gap-2.5 rounded-md border border-border/20 bg-background/30 py-1.5 pr-2.5 pl-[9px] text-sm text-muted-foreground backdrop-blur-md ring-1 ring-white/5 transition-all hover:border-border/40 hover:bg-background/50"
          aria-label="Search"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left text-sm">Find…</span>
          <KbdCombo keys={['meta', 'k']} className="hidden sm:inline-flex" />
        </button>
      </div>

      {navStyle === 'drill' ? (
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.div
              key={viewKey}
              className="absolute inset-0 flex flex-col"
              initial={variants.initial}
              animate={variants.animate}
              exit={variants.exit}
              transition={slideTransition}
            >
              {activeDrillGroup ? (
                <>
                  <NavDrillContent
                    group={activeDrillGroup}
                    onBack={closeDrill}
                    subDrillBasePaths={subDrillBasePaths}
                  />
                  {/* Keep the bottom drill groups and any plugin-contributed
                    bottom items pinned to the footer while a sub-menu is open
                    so they stay reachable without first backing out of the
                    drill. */}
                  <NavBottomSection
                    pluginBottomItems={pluginBottomItems}
                    bottomDrillGroups={bottomDrillGroups}
                    activeDrillGroup={urlDrillGroup ?? null}
                  />
                </>
              ) : (
                <>
                  <div className="flex flex-1 min-h-0 flex-col gap-0.5 px-1.5 scrollbar-fade">
                    <NavMainLinks items={allMainNavItems} />

                    {mainDrillGroups.map((group) => (
                      <NavDrillItem
                        key={group.basePath}
                        group={group}
                        isActive={urlDrillGroup === group}
                      />
                    ))}
                  </div>

                  <NavBottomSection
                    pluginBottomItems={pluginBottomItems}
                    bottomDrillGroups={bottomDrillGroups}
                    activeDrillGroup={urlDrillGroup ?? null}
                  />
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      ) : navStyle === 'expand' ? (
        <NavExpandView
          allMainNavItems={allMainNavItems}
          mainDrillGroups={mainDrillGroups}
          bottomDrillGroups={bottomDrillGroups}
          pluginBottomItems={pluginBottomItems}
          drillGroups={drillGroups}
        />
      ) : navStyle === 'flat' ? (
        <NavFlatView
          allMainNavItems={allMainNavItems}
          mainDrillGroups={mainDrillGroups}
          bottomDrillGroups={bottomDrillGroups}
          pluginBottomItems={pluginBottomItems}
          drillGroups={drillGroups}
        />
      ) : (
        <NavFlyoutView
          allMainNavItems={allMainNavItems}
          mainDrillGroups={mainDrillGroups}
          bottomDrillGroups={bottomDrillGroups}
          pluginBottomItems={pluginBottomItems}
          drillGroups={drillGroups}
        />
      )}

      <div className="px-1.5 pb-1">
        <OrgSwitcher />
      </div>
      <div className="mx-3 h-px bg-border/30" />
      <div className="px-1.5 py-2">
        <UserSection />
      </div>
    </nav>
  );
}
